import { createSlice } from '@reduxjs/toolkit'
import { message } from 'antd'
import API from '../../axios'

const initialState = {
	aliases: [],
	heroPlayers: [],
	villainPlayers: [],

	isMainSectionLoading: false,
	isSlaveSectionLoading: false,
	isHelperSectionLoading: false,

	activeAliasIndex: null,
	activeAliasId: null,
	activeAliasType: null,
	activeAlias: null,
	editingAliasIndex: null,

	isCreatingNewAlias: false,
	isEditingAlias: false,

	isModalInvoked: false,
	modalAction: 'delete', // default action for modal
	isModalLoading: false,

	// helpers for handling change of this params for alias instance in first table
	activeAliasColor: '',
	activeAliasName: '',

	// helpers for displaying picker in first table alias item component
	isColorPickerVisible: false,
	colorPickerPosition: {
		xOffset: 0,
		yOffset: 0,
	},
}

const slice = createSlice({
	name: 'aliases',
	initialState,
	reducers: {
		// sets aliases array on pages init
		setAliases(state, action) {
			state.aliases = action.payload
		},
		// filters all players and sets villain only players to corresponding value
		setVillainPlayers(state, action) {
			state.villainPlayers = action.payload
		},
		// filters all players and sets hero only players to corresponding value
		setHeroPlayers(state, action) {
			state.heroPlayers = action.payload
		},

		// removes alias with specified index
		removeAlias(state) {
			state.aliases = state.aliases.filter((item, index) => index !== state.activeAliasIndex)
		},

		setColorPickerVisible(state, action) {
			state.isColorPickerVisible = action.payload
		},

		setColorPickerPosition(state, action) {
			const [xOffset, yOffset] = action.payload
			state.colorPickerPosition.xOffset = xOffset
			state.colorPickerPosition.yOffset = yOffset
		},

		setEditingAliasIndex(state, action) {
			state.editingAliasIndex = action.payload
		},
		setEditingAlias(state, action) {
			state.isEditingAlias = action.payload
		},
		setCreatingNewAlias(state, action) {
			state.isCreatingNewAlias = action.payload
		},

		setActiveAliasColorCode(state, action) {
			state.activeAliasColor = action.payload
		},
		setActiveAliasName(state, action) {
			state.activeAliasName = action.payload
		},
		setAliasColor(state, action) {
			const [value, index] = action.payload
			state.aliases[index].color_code = value
		},
		setAliasName(state, action) {
			const [value, index] = action.payload
			state.aliases[index].alias_name = value
		},

		setActiveAliasType(state, action) {
			state.activeAliasType = action.payload
		},
		setActiveAliasId(state, action) {
			state.activeAliasId = action.payload
		},
		setActiveAliasIndex(state, action) {
			state.activeAliasIndex = action.payload
			if (state.editingAliasIndex !== state.activeAliasIndex) {
				state.editingAliasIndex = null
			}
		},
		setActiveAlias(state, action) {
			state.activeAlias = action.payload
		},

		setModalLoading(state, action) {
			state.isModalLoading = action.payload
		},
		setModalAction(state, action) {
			state.modalAction = action.payload
		},
		setModalInvoked(state, action) {
			state.isModalInvoked = action.payload
		},

		setLoading(state, action) {
			const [sectionID, loadingState] = action.payload
			switch (sectionID) {
				case 1:
					state.isMainSectionLoading = loadingState
					break
				case 2:
					state.isSlaveSectionLoading = loadingState
					break
				case 3:
					state.isHelperSectionLoading = loadingState
					break
			}
		},

		addNewAlias(state, action) {
			state.aliases.push({
				alias_name: '',
				color: '#fff',
				aliases: [],
				aliasesArr: [],
				is_hero: action.payload === 'H',
			})
			state.activeAliasIndex = state.aliases.length - 1
		},

		saveNewAlias(state, action) {
			state.aliases[state.aliases.length - 1] = action.payload
		},

		removePlayersFromAlias(state, action) {
			const [players, index] = action.payload
			state.aliases[index].aliases = state.aliases[index].aliases.filter(
				(item) => !players.map((item) => item.name).includes(item)
			)
		},
		addPlayersToAlias(state, action) {
			state.aliases[state.activeAliasIndex].aliases = action.payload
		},

		// action performed on pages leave (binded to section unmount)
		resetAllActiveInstances(state) {
			state.activeAliasIndex = null
			state.activeAlias = null
			state.activeAliasId = null
			state.activeAliasType = null
		},
		removeLastAlias(state) {
			state.aliases.pop()
			state.isCreatingNewAlias = false
		},
	},
})

export const { actions, reducer } = slice

export const setActiveAliasColorCode = (color) => (dispatch) => dispatch(actions.setActiveAliasColorCode(color))
export const setActiveAliasName = (name) => (dispatch) => dispatch(actions.setActiveAliasName(name))
export const setEditingAlias = (status) => (dispatch) => dispatch(actions.setEditingAlias(status))
export const setEditingAliasIndex = (index) => (dispatch) => dispatch(actions.setEditingAliasIndex(index))
export const setPickerPosition = (x, y) => (dispatch) => dispatch(actions.setColorPickerPosition([x, y]))
export const togglePicker = () => (dispatch, getState) =>
	dispatch(actions.setColorPickerVisible(!getState().aliases.isColorPickerVisible))
export const removeLastAlias = () => (dispatch) => dispatch(actions.removeLastAlias())
export const closeModal = () => (dispatch) => dispatch(actions.setModalInvoked(false))

/**
 * Function that toggles modal and sets active item id for deletion
 * */
export const toggleModalToRemoveAlias = (aliasId) => (dispatch) => {
	dispatch(actions.setActiveAliasId(aliasId))
	dispatch(actions.setModalAction('delete'))
	dispatch(actions.setModalInvoked(true))
}

/**
 * Function that adds virtual alias to current list of aliases
 * @param {String} type - type of added alias (HERO OR VILLAIN)
 * */
export const addNewAlias = (type) => (dispatch) => {
	dispatch(actions.addNewAlias(type))
	dispatch(actions.setActiveAliasColorCode('#fff'))
	dispatch(actions.setActiveAliasName(''))
	dispatch(actions.setActiveAliasType(null))
	dispatch(actions.setCreatingNewAlias(true))
}

/**
 * Function that adds new players to alias
 * @param {Array} players - array of players objects
 * */
export const addPlayersToAlias = (players) => async (dispatch, getState) => {
	const { aliases, activeAliasIndex, activeAlias } = getState().aliases
	try {
		dispatch(actions.setLoading([2, true]))
		// API requires only name of players, so other fields are excluded
		const namesToBeAdded = players.map((item) => item.name)
		const filteredPlayers = namesToBeAdded.filter((item) => !aliases[activeAliasIndex].aliases.includes(item))
		const newPlayers = aliases[activeAliasIndex].aliases.concat(filteredPlayers)
		dispatch(actions.addPlayersToAlias(newPlayers))
		await API.post('update_alias', {
			updated_alias: {
				...activeAlias,
				aliases: newPlayers,
			},
		})
		dispatch(actions.setLoading([2, false]))
	} catch (e) {
		dispatch(actions.setLoading([2, false]))
	}
}

export const removeAlias = () => async (dispatch, getState) => {
	const { activeAliasId } = getState().aliases
	try {
		dispatch(actions.setModalLoading(true))
		const response = await API.post('remove_alias', {
			alias_id: activeAliasId,
		})
		// todo: remove this thumbnail later
		dispatch(actions.setAliases([]))
		if (response.data.status === 'ok') {
			dispatch(fetchAliases())
		}
		dispatch(actions.setModalLoading(false))
		dispatch(actions.setModalInvoked(false))
		dispatch(actions.setActiveAliasType(null))
		dispatch(actions.setCreatingNewAlias(false))
		dispatch(actions.setActiveAliasIndex(null))
		dispatch(actions.setEditingAliasIndex(null))
		dispatch(activeAliasId.setActiveAlias(null))
		dispatch(actions.resetAllActiveInstances())
	} catch (e) {
		dispatch(actions.setModalLoading(false))
		dispatch(actions.setModalInvoked(false))
	}
}

export const removePlayersFromAlias = (players) => async (dispatch, getState) => {
	try {
		const { activeAlias, activeAliasIndex } = getState().aliases
		dispatch(actions.setLoading([2, true]))
		const response = await API.post('remove_name_from_alias', {
			alias: {
				...activeAlias,
				aliases: players.map((item) => item.name),
			},
		})
		if (response.data.status === 'ok') {
			dispatch(actions.removePlayersFromAlias([players, activeAliasIndex]))
		}
		dispatch(actions.setLoading([2, false]))
	} catch (e) {
		dispatch(actions.setLoading([2, false]))
		console.log('ERROR HAS OCCURRED WHILE REMOVING PLAYERS FROM CURRENT ALIAS: ', e)
	}
}
/**
 * Function that sets active alias attributes
 * @param alias {Object}
 * @param index {Number}
 * */
export const setActiveAlias = (alias, index) => (dispatch, getState) => {
	const { editingAliasIndex, activeAliasIndex } = getState().aliases
	const { alias_id, is_hero, color_code } = alias

	dispatch(actions.setActiveAliasColorCode(color_code))
	dispatch(actions.setActiveAliasName(alias.alias_name))

	dispatch(actions.setActiveAlias(alias))
	dispatch(actions.setActiveAliasIndex(index))
	dispatch(actions.setActiveAliasId(alias_id))
	dispatch(actions.setActiveAliasType(is_hero ? 'H' : 'V'))

	// dispatch(actions.setActiveAliasColorCode(color_code));
}

/**
 * Function that fetches all saved aliases for user
 * */
export const fetchAliases = () => async (dispatch) => {
	try {
		dispatch(actions.setLoading([1, true]))
		const response = await API.get('get_aliases_for_user')
		dispatch(actions.setAliases(response.data))
		dispatch(actions.setLoading([1, false]))
	} catch (e) {
		dispatch(actions.setLoading([1, false]))
	}
}

/**
 * Function that creates new alias, make query to API and saves new alias item
 * */
export const createNewAlias = (data) => async (dispatch, getState) => {
	const { chosenPerson, name } = data
	const { activeAliasColor } = getState().aliases
	try {
		dispatch(actions.setLoading([1, true]))
		const isHero = chosenPerson === 'H'
		const response = await API.post('create_new_alias', {
			alias: {
				isHero,
				aliasName: name,
				color: activeAliasColor,
				aliases: null,
				aliasesArr: [],
			},
		})

		dispatch(actions.setCreatingNewAlias(false))
		// creating new alias item to prevent making new query to API
		const newAliasItem = {
			is_hero: chosenPerson === 'H',
			alias_name: name,
			color_code: activeAliasColor,
			alias_id: response.data.code,
			aliases: [],
		}
		// if saved successfully to DB,
		if (response.data.status === 'ok') {
			dispatch(actions.saveNewAlias(newAliasItem))
			dispatch(actions.setActiveAlias(newAliasItem))
			dispatch(actions.setActiveAliasType(chosenPerson))
		}
		dispatch(actions.setLoading([1, false]))
	} catch (e) {
		dispatch(actions.setLoading([1, false]))
		// API return 400 code error in case we have added already existing is db alias name
		if (e.response.status === 400) {
			message.error('ALIAS WITH THIS NAME ALREADY EXISTS')
		}
	}
}

/**
 * Function that once fetches all available players and fills store with villain and hero players
 * */
export const fetchPlayers = () => async (dispatch, getState) => {
	try {
		const { villainPlayers, heroPlayers } = getState().aliases
		// prevent extra queries if data is already fetched
		if (!(villainPlayers.length > 0 || heroPlayers.length > 0)) {
			dispatch(actions.setLoading([3, true]))
			const response = await API.get('get_names')

			// we have to add additional keys to villain item due to antd table renderer requirements
			const villain = response.data
				.filter((item) => item.is_hero === false)[0]
				.names.map((item) => {
					item.key = item.name

					return item
				})
			// we have to add additional keys to hero item due to antd table renderer requirements
			const hero = response.data
				.filter((item) => item.is_hero === true)[0]
				.names.map((item) => {
					item.key = item.name

					return item
				})

			dispatch(actions.setVillainPlayers(villain))
			dispatch(actions.setHeroPlayers(hero))
			dispatch(actions.setLoading([3, false]))
		}
	} catch (e) {
		console.log('GOT ERROR WHILE FETCHING ALL PLAYERS: ', e)
		dispatch(actions.setLoading([3, false]))
	}
}

/**
 * Updates alias color and name
 * @param record {Object}
 * @param data {Object}
 * @param index {Number}
 * */
export const updateAlias = (record, data, index) => async (dispatch, getState) => {
	try {
		// todo: set to frontend alias current values
		const { activeAliasColor } = getState().aliases
		dispatch(actions.setLoading([1, true]))
		const { name } = data

		dispatch(actions.setAliasColor([activeAliasColor, index]))
		dispatch(actions.setAliasName([name, index]))

		await API.post('update_alias', {
			updated_alias: {
				...record,
				color_code: activeAliasColor,
				alias_name: name,
			},
		})
		dispatch(actions.setLoading([1, false]))
	} catch (e) {
		dispatch(actions.setLoading([1, false]))
	}
}
